import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GalleryPage = () => {
  const { t, language } = useLanguage();
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API}/gallery`);
        setGalleryItems(response.data);
      } catch (error) {
        console.error('Error fetching gallery:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  return (
    <main data-testid="gallery-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#F2F0E9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <span className="font-accent text-lg text-[#8DA399]">
            {t('gallery.subtitle')}
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#2C3333] mt-4">
            {t('gallery.title')}
          </h1>
          <p className="mt-6 font-sans text-lg text-[#5C5C5C] max-w-2xl mx-auto">
            {language === 'en'
              ? 'Witness the transformation of spaces from chaos to serenity. Every project tells a story of renewed peace and functionality.'
              : 'Observa la transformación de espacios del caos a la serenidad. Cada proyecto cuenta una historia de paz y funcionalidad renovadas.'}
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 md:py-24 bg-[#FAFAF9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-8 h-8 border-4 border-[#8DA399] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-24">
              {galleryItems.map((item, index) => (
                <div
                  key={item.id}
                  data-testid={`gallery-item-${item.id}`}
                  className="border border-[#E5E5E0] bg-white p-6 md:p-10"
                >
                  {/* Title */}
                  <div className="text-center mb-10">
                    <h2 className="font-serif text-2xl md:text-3xl text-[#2C3333]">
                      {language === 'en' ? item.title_en : item.title_es}
                    </h2>
                    <p className="mt-4 font-sans text-[#5C5C5C] max-w-2xl mx-auto">
                      {language === 'en' ? item.description_en : item.description_es}
                    </p>
                  </div>

                  {/* Before/After Images */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                    {/* Before */}
                    <div className="relative group">
                      <div className="overflow-hidden">
                        <img
                          src={item.before_image}
                          alt={`Before - ${language === 'en' ? item.title_en : item.title_es}`}
                          className="w-full h-[300px] md:h-[400px] object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="absolute top-4 left-4 bg-[#2C3333] text-white px-4 py-2 font-sans text-sm tracking-wide">
                        {t('gallery.before')}
                      </div>
                    </div>

                    {/* After */}
                    <div className="relative group">
                      <div className="overflow-hidden">
                        <img
                          src={item.after_image}
                          alt={`After - ${language === 'en' ? item.title_en : item.title_es}`}
                          className="w-full h-[300px] md:h-[400px] object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="absolute top-4 left-4 bg-[#8DA399] text-white px-4 py-2 font-sans text-sm tracking-wide">
                        {t('gallery.after')}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#8DA399]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white">
            {language === 'en'
              ? 'Ready for Your Own Transformation?'
              : '¿Lista para Tu Propia Transformación?'}
          </h2>
          <p className="mt-6 font-sans text-lg text-white/80 max-w-2xl mx-auto">
            {language === 'en'
              ? 'Let\'s create a serene space that reflects who you are and supports how you live.'
              : 'Creemos un espacio sereno que refleje quién eres y apoye cómo vives.'}
          </p>
          <div className="mt-10">
            <Link to="/contact">
              <Button
                data-testid="gallery-cta-button"
                className="bg-white text-[#8DA399] hover:bg-[#F2F0E9] rounded-full px-10 py-6 text-lg shadow-lg"
              >
                {t('services.requestQuote')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default GalleryPage;
